/**
 * LaTeX Bibliography Utilities
 * Handles detection and management of bibliography types (internal vs external)
 */

export type BibliographyType = 'internal' | 'external' | 'none'

export interface BibliographyInfo {
  type: BibliographyType
  bibliographyStart?: number // Position of \begin{thebibliography} or \bibliography{}
  bibliographyEnd?: number // Position of \end{thebibliography}
  bibitemCount?: number // Number of existing \bibitem entries
}

/**
 * Detect bibliography type in LaTeX document
 */
export function detectBibliographyType(content: string): BibliographyInfo {
  // Check for external bibliography (uses .bib file)
  // Look for \bibliography{filename} or \addbibresource{filename.bib} or \printbibliography
  const externalPatterns = [
    /\\bibliography\{[^}]+\}/i,
    /\\addbibresource\{[^}]+\}/i,
    /\\printbibliography/i
  ]
  const hasExternal = externalPatterns.some(pattern => pattern.test(content))

  // Check for internal bibliography (uses \begin{thebibliography}...\end{thebibliography})
  const internalStartPattern = /\\begin\{thebibliography\}/i
  const internalEndPattern = /\\end\{thebibliography\}/i
  const internalStartMatch = content.match(internalStartPattern)
  const internalEndMatch = content.match(internalEndPattern)
  const hasInternal = internalStartMatch !== null && internalEndMatch !== null

  // External bibliography takes precedence if both exist
  if (hasExternal) {
    return { type: 'external' }
  }

  if (hasInternal && internalStartMatch && internalEndMatch) {
    const startPos = internalStartMatch.index || 0
    const endPos = internalEndMatch.index || content.length
    
    // Count existing \bibitem entries
    const bibliographySection = content.substring(startPos, endPos)
    const bibitemMatches = bibliographySection.match(/\\bibitem\{[^}]+\}/gi)
    const bibitemCount = bibitemMatches ? bibitemMatches.length : 0

    return {
      type: 'internal',
      bibliographyStart: startPos,
      bibliographyEnd: endPos,
      bibitemCount
    }
  }

  return { type: 'none' }
}

/**
 * Generate BibTeX entry for internal bibliography
 */
export function generateBibitemEntry(
  paper: {
    title?: string
    authors?: string[]
    year?: number
    venue?: string
    doi?: string
    url?: string
  },
  bibtexKey: string
): string {
  // Validate inputs
  if (!paper) {
    throw new Error('Paper object is required')
  }
  
  if (!bibtexKey || typeof bibtexKey !== 'string') {
    throw new Error('bibtexKey must be a non-empty string')
  }

  // Format authors
  let authorsStr = 'Unknown Author'
  if (paper.authors && Array.isArray(paper.authors) && paper.authors.length > 0) {
    if (paper.authors.length === 1) {
      authorsStr = String(paper.authors[0] || 'Unknown Author')
    } else if (paper.authors.length <= 3) {
      authorsStr = paper.authors.map(a => String(a || '')).filter(a => a).join(', ')
    } else {
      authorsStr = `${String(paper.authors[0] || 'Unknown')} et al.`
    }
  }

  // Format citation entry
  // Format: \bibitem{key} Author(s), ``Title,'' Venue Year.
  const title = String(paper.title || 'Untitled')
  const year = paper.year ? String(paper.year) : 'n.d.'
  const venue = String(paper.venue || 'Journal')
  
  // Use string concatenation to avoid template literal parsing issues with backticks
  const openingQuote = '``'
  const closingQuote = "''"
  let entry = '\\bibitem{' + bibtexKey + '} ' + authorsStr + ', ' + openingQuote + title + closingQuote + ' ' + venue + ' ' + year + '.'
  
  // Add DOI or URL if available
  if (paper.doi) {
    entry += ' DOI: ' + String(paper.doi) + '.'
  } else if (paper.url) {
    entry += ' ' + String(paper.url) + '.'
  }

  return entry
}

/**
 * Insert \bibitem entry into internal bibliography section
 */
export function insertBibitemIntoDocument(
  content: string,
  bibitemEntry: string,
  bibliographyInfo: BibliographyInfo
): string {
  if (bibliographyInfo.type !== 'internal' || bibliographyInfo.bibliographyEnd === undefined) {
    return content
  }

  // Find the position just before \end{thebibliography}
  const insertPosition = bibliographyInfo.bibliographyEnd
  
  // Insert the bibitem entry with proper formatting
  const formattedEntry = `  ${bibitemEntry}\n`
  
  const before = content.substring(0, insertPosition)
  const after = content.substring(insertPosition)
  
  return before + formattedEntry + after
}

/**
 * Check if document needs bibliography environment
 */
export function needsBibliographyEnvironment(content: string): boolean {
  // Check if \cite commands exist but no bibliography is defined
  const hasCite = /\\cite\{[^}]+\}/i.test(content)
  const hasBibliography = /\\bibliography\{[^}]+\}|\\begin\{thebibliography\}/i.test(content)
  
  return hasCite && !hasBibliography
}

